import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { organizationRepository } from "../repositories";
import { normalizeOrganizationBrandTheme } from "../models";
import { fetchGoogleFontFamilies } from "@infrastructure/api/google-fonts.api";
import { extractApiErrorMessage } from "@/utils/api-error";
const CACHE_DURATION = 5 * 60 * 1000;
export const useOrganizationStore = defineStore("organizations", () => {
    const organizations = ref([]);
    const members = ref({});
    const bankAccounts = ref({});
    const brandThemes = ref({});
    const fonts = ref([]);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const isLoadingFonts = ref(false);
    const error = ref(null);
    const fontsError = ref(null);
    const lastFetched = ref(null);
    const isCacheValid = computed(() => {
        if (!lastFetched.value)
            return false;
        return Date.now() - lastFetched.value.getTime() < CACHE_DURATION;
    });
    const activeOrganizations = computed(() => organizations.value.filter((organization) => organization.is_active));
    const byId = computed(() => (id) => organizations.value.find((organization) => organization.id === id) ?? null);
    async function fetchAll(forceRefresh = false) {
        if (!forceRefresh && isCacheValid.value)
            return organizations.value;
        isLoading.value = true;
        error.value = null;
        try {
            const data = await organizationRepository.getAll();
            organizations.value = data;
            lastFetched.value = new Date();
            return data;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al cargar organizaciones";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function fetchOrganization(id, options = {}) {
        isLoading.value = true;
        error.value = null;
        try {
            const organization = await organizationRepository.getOne(id, options);
            upsertOrganization(organization);
            return organization;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al cargar organización";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function createOrganization(payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const organization = await organizationRepository.create(payload);
            organizations.value = [organization, ...organizations.value];
            return organization;
        }
        catch (err) {
            error.value = extractApiErrorMessage(err, "Error al crear organización");
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function updateOrganization(id, payload, options = {}) {
        isSaving.value = true;
        error.value = null;
        try {
            const organization = await organizationRepository.update(id, payload, options);
            upsertOrganization(organization);
            return organization;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al actualizar organización";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    function upsertOrganization(organization) {
        const exists = organizations.value.some((item) => item.id === organization.id);
        organizations.value = exists
            ? organizations.value.map((item) => item.id === organization.id ? organization : item)
            : [organization, ...organizations.value];
    }
    async function deactivateOrganization(id, payload = {}) {
        isSaving.value = true;
        error.value = null;
        try {
            const organization = await organizationRepository.deactivate(id, payload);
            organizations.value = organizations.value.map((item) => item.id === id ? organization : item);
            return organization;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function reactivateOrganization(id, payload = {}) {
        isSaving.value = true;
        error.value = null;
        try {
            const organization = await organizationRepository.reactivate(id, payload);
            organizations.value = organizations.value.map((item) => item.id === id ? organization : item);
            return organization;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function softDeleteOrganization(id, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const organization = await organizationRepository.softDelete(id, payload);
            organizations.value = organizations.value.map((item) => item.id === id ? organization : item);
            return organization;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function fetchBankAccounts(orgId, options = {}) {
        isLoading.value = true;
        error.value = null;
        try {
            const data = await organizationRepository.getBankAccounts(orgId, options);
            bankAccounts.value = { ...bankAccounts.value, [orgId]: data };
            return data;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al cargar cuentas bancarias";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function addBankAccount(orgId, payload, options = {}) {
        isSaving.value = true;
        error.value = null;
        try {
            const account = await organizationRepository.addBankAccount(orgId, payload, options);
            bankAccounts.value = {
                ...bankAccounts.value,
                [orgId]: [...(bankAccounts.value[orgId] ?? []), account],
            };
            return account;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al crear cuenta bancaria";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function updateBankAccount(orgId, bankAccountId, payload, options = {}) {
        isSaving.value = true;
        error.value = null;
        try {
            const account = await organizationRepository.updateBankAccount(orgId, bankAccountId, payload, options);
            bankAccounts.value = {
                ...bankAccounts.value,
                [orgId]: (bankAccounts.value[orgId] ?? []).map((item) => item.id === bankAccountId ? account : item),
            };
            return account;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al actualizar cuenta bancaria";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function removeBankAccount(orgId, bankAccountId, options = {}) {
        isSaving.value = true;
        error.value = null;
        try {
            await organizationRepository.removeBankAccount(orgId, bankAccountId, options);
            bankAccounts.value = {
                ...bankAccounts.value,
                [orgId]: (bankAccounts.value[orgId] ?? []).filter((account) => account.id !== bankAccountId),
            };
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al remover cuenta bancaria";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function fetchBrandTheme(orgId, options = {}) {
        isLoading.value = true;
        error.value = null;
        try {
            const data = normalizeOrganizationBrandTheme(await organizationRepository.getBrandTheme(orgId, options));
            brandThemes.value = { ...brandThemes.value, [orgId]: data };
            return data;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al cargar tema de marca";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function updateBrandTheme(orgId, payload, options = {}) {
        isSaving.value = true;
        error.value = null;
        try {
            const theme = normalizeOrganizationBrandTheme(await organizationRepository.updateBrandTheme(orgId, payload, options));
            brandThemes.value = { ...brandThemes.value, [orgId]: theme };
            return theme;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al guardar tema de marca";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function fetchGoogleFonts() {
        isLoadingFonts.value = true;
        fontsError.value = null;
        try {
            fonts.value = await fetchGoogleFontFamilies();
        }
        catch (err) {
            fontsError.value =
                err instanceof Error ? err.message : "Error al cargar fuentes";
        }
        finally {
            isLoadingFonts.value = false;
        }
    }
    async function fetchMembers(orgId) {
        isLoading.value = true;
        error.value = null;
        try {
            const data = await organizationRepository.getMembers(orgId);
            members.value = { ...members.value, [orgId]: data };
            return data;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al cargar usuarios";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function addMember(orgId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const member = await organizationRepository.addMember(orgId, payload);
            members.value = {
                ...members.value,
                [orgId]: [member, ...(members.value[orgId] ?? [])],
            };
            return member;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al crear usuario";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function updateMember(orgId, userId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const member = await organizationRepository.updateMember(orgId, userId, payload);
            members.value = {
                ...members.value,
                [orgId]: (members.value[orgId] ?? []).map((item) => item.user_id === userId ? member : item),
            };
            return member;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al actualizar usuario";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function removeMember(orgId, userId) {
        isSaving.value = true;
        error.value = null;
        try {
            await organizationRepository.removeMember(orgId, userId);
            members.value = {
                ...members.value,
                [orgId]: (members.value[orgId] ?? []).filter((member) => member.user_id !== userId),
            };
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al remover usuario";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    function clearMemberCache() {
        members.value = {};
    }
    function clearBankAccountCache() {
        bankAccounts.value = {};
    }
    function clearBrandThemeCache() {
        brandThemes.value = {};
    }
    function $reset() {
        organizations.value = [];
        members.value = {};
        bankAccounts.value = {};
        brandThemes.value = {};
        fonts.value = [];
        isLoading.value = false;
        isSaving.value = false;
        isLoadingFonts.value = false;
        error.value = null;
        fontsError.value = null;
        lastFetched.value = null;
    }
    return {
        organizations,
        members,
        bankAccounts,
        brandThemes,
        fonts,
        isLoading,
        isSaving,
        isLoadingFonts,
        error,
        fontsError,
        lastFetched,
        isCacheValid,
        activeOrganizations,
        byId,
        fetchAll,
        fetchOrganization,
        createOrganization,
        updateOrganization,
        deactivateOrganization,
        reactivateOrganization,
        softDeleteOrganization,
        fetchBankAccounts,
        addBankAccount,
        updateBankAccount,
        removeBankAccount,
        fetchBrandTheme,
        updateBrandTheme,
        fetchGoogleFonts,
        fetchMembers,
        addMember,
        updateMember,
        removeMember,
        clearMemberCache,
        clearBankAccountCache,
        clearBrandThemeCache,
        $reset,
    };
});
